import { AccountPage, AppAccountsPage, ShellLayout, UsersPage } from "@dl/web-core-js/shell";
import { createRootRoute, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { HomePage } from "./pages/HomePage";
import { ApiInventoryUpdatesPage } from "./pages/ApiInventoryUpdatesPage";
import { SalesChannelsPage } from "./pages/SalesChannelsPage";
import { ShopifyStorePage } from "./pages/ShopifyStorePage";
import { ShopifyTagSourcePage } from "./pages/config/shopifyTagSourcePage";
import { PipelinePage } from "./pages/PipelinePage";
import { TransferPlanPage } from "./pages/TransferPlanPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { ClientMarginPage } from "./pages/ClientMarginPage";
import { SalesAnalysisPage } from "./pages/SalesAnalysisPage";
import { ProductDetailPage } from "./pages/ProductDetailPage";
import { VersionRulesPage } from "./pages/config/VersionRulesPage";
import { InvoicesPage } from "./pages/InvoicesPage";
import { OrdersPage } from "./pages/OrdersPage";
import { ProductsPage } from "./pages/ProductsPage";
import {
  CategoryPage,
  CogsExpensePage,
  CollectionPage,
  DesignerPage,
  MetafieldConfigPage,
  TagPage,
} from "./pages/config/configPages";
import {
  AttributePage,
  DepartmentPage,
  MeasurementKindPage,
  OperationPage,
  PartGroupPage,
  SitePage,
  StationPage,
  ProcessPage,
  StockAdjustmentReasonPage,
  UnitPage,
} from "./pages/config/manufacturingConfigPages";
import { SettingsPage } from "./pages/config/SettingsPage";
import { ShopFloorSetupPage } from "./pages/config/ShopFloorSetupPage";
import { AutomaticDiscontinuationPage, CogsConfigPage } from "./pages/config/specialConfigPages";
import { ManualInvoicesPage } from "./pages/manualinvoice/ManualInvoicesPage";
import { PartsPage } from "./pages/parts/PartsPage";
import { ManufacturingPartsPage } from "./pages/manufacturing/PartsPage";
import { RoutingReviewPage } from "./pages/manufacturing/RoutingReviewPage";
import { CleanupPage } from "./pages/configurator/CleanupPage";
import { ColoursPage } from "./pages/configurator/ColoursPage";
import { ProductModelsPage } from "./pages/configurator/ProductModelsPage";
import { CutJobDetailPage } from "./pages/production/cutjobs/CutJobDetailPage";
import { CutJobsPage } from "./pages/production/cutjobs/CutJobsPage";
import { ManufacturingPartDetailPage } from "./pages/manufacturing/PartDetailPage";
import { SupplierDetailPage } from "./pages/manufacturing/SupplierDetailPage";
import { SuppliersPage } from "./pages/manufacturing/SuppliersPage";
import { StockPage } from "./pages/manufacturing/StockPage";
import { StockAdjustmentPage } from "./pages/manufacturing/StockAdjustmentPage";
import { PurchaseOrdersPage } from "./pages/purchasing/PurchaseOrdersPage";
import { PurchaseOrderDetailPage } from "./pages/purchasing/PurchaseOrderDetailPage";
import { PurchaseOrderPrintPage } from "./pages/purchasing/PurchaseOrderPrintPage";
import { CashProjectionPage } from "./pages/purchasing/CashProjectionPage";
import { PurchasingReviewsPage } from "./pages/purchasing/PurchasingReviewsPage";
import { ReplenishmentPage } from "./pages/purchasing/replenishment/ReplenishmentPage";
import { OrderUnitsPage } from "./pages/purchasing/OrderUnitsPage";
import { ProductionOrdersPage } from "./pages/production/ProductionOrdersPage";
import { ScrapReportPage } from "./pages/production/ScrapReportPage";
import { VirtualPartsPage } from "./pages/config/VirtualPartsPage";
import { ShopFloorBoardPage } from "./pages/floor/ShopFloorBoardPage";
import { StationKioskPage } from "./pages/floor/StationKioskPage";
import { ProductionOrderDetailPage } from "./pages/production/ProductionOrderDetailPage";
import { StockCountsPage } from "./pages/manufacturing/StockCountsPage";
import { StockCountPage } from "./pages/manufacturing/StockCountPage";
import { StockTransferPage } from "./pages/manufacturing/StockTransferPage";
import { InactiveStockPage } from "./pages/manufacturing/InactiveStockPage";
import { StockingPointsPage } from "./pages/manufacturing/StockingPointsPage";
import { ShelvesPage } from "./pages/production/ShelvesPage";
import { ServiceOrdersPage } from "./pages/servicedesk/ServiceOrdersPage";
import { ServiceOrderDetailPage } from "./pages/servicedesk/ServiceOrderDetailPage";
import { ServiceProductionQueuePage } from "./pages/servicedesk/ServiceProductionQueuePage";
import { TerrebonnePage } from "./pages/production/TerrebonnePage";
import { ServiceMotivePage } from "./pages/config/serviceDeskConfigPages";
import { ServicesPage } from "./pages/services/ServicesPage";
import { ServiceDetailPage } from "./pages/services/ServiceDetailPage";
import { PartDetailPage } from "./pages/parts/PartDetailPage";
import { PricingPage } from "./pages/pricing/PricingPage";
import { ProductDiscountPage } from "./pages/pricing/ProductDiscountPage";
import { PricingRulesPage } from "./pages/pricing/PricingRulesPage";
import { PlaceholderPage } from "./pages/PlaceholderPage";
import { ActionsPage } from "./pages/admin/ActionsPage";

// The shell is the root route so the sidebar's links resolve against this
// router. Every page below renders into its <Outlet/>.
const rootRoute = createRootRoute({ component: ShellLayout });

/**
 * Detail pages keep their open tab in the URL (`?tab=bom`, see EditPane's
 * urlParam), so Back returns to the tab you were on and a link can open a
 * record on a given tab. Declared here so those links are typed.
 */
function tabSearch(search: Record<string, unknown>): { tab?: string } {
  return typeof search.tab === "string" && search.tab !== "" ? { tab: search.tab } : {};
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});

const stubs: Array<{ path: string; titleKey: string; view: string }> = [];

// First screen ported off a placeholder — live against GET /account.
const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/users",
  component: UsersPage,
});

const actionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/actions",
  component: ActionsPage,
});

const clientMarginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/client-margin",
  component: ClientMarginPage,
});

const salesAnalysisRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/analysis",
  component: SalesAnalysisPage,
});

const invoicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/invoices",
  component: InvoicesPage,
});

const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/orders",
  component: OrdersPage,
});

const configRoutes = (
  [
    ["/config/settings", SettingsPage],
    ["/config/product/designer", DesignerPage],
    ["/config/product/collection", CollectionPage],
    ["/config/product/category", CategoryPage],
    ["/config/product/tag", TagPage],
    ["/config/product/metafield", MetafieldConfigPage],
    ["/config/product/cogs", CogsConfigPage],
    ["/config/cogs/expense", CogsExpensePage],
    ["/config/automatic-product-discontinuation", AutomaticDiscontinuationPage],
    ["/config/shopify/tag-source", ShopifyTagSourcePage],
    // Manufacturing reference data — the SIGM MFI replacement's master data.
    ["/config/manufacturing/unit", UnitPage],
    ["/config/manufacturing/measurement-kind", MeasurementKindPage],
    ["/config/manufacturing/site", SitePage],
    ["/config/manufacturing/department", DepartmentPage],
    ["/config/manufacturing/operation", OperationPage],
    ["/config/manufacturing/process", ProcessPage],
    ["/config/manufacturing/station", StationPage],
    ["/config/manufacturing/part-group", PartGroupPage],
    ["/config/manufacturing/attribute", AttributePage],
    ["/config/manufacturing/virtual-part", VirtualPartsPage],
    ["/config/manufacturing/version-rule", VersionRulesPage],
    ["/config/manufacturing/stock-adjustment-reason", StockAdjustmentReasonPage],
    ["/config/servicedesk/motive", ServiceMotivePage],
  ] as const
).map(([path, component]) =>
  createRoute({ getParentRoute: () => rootRoute, path, component }),
);

/** Operators, scrap reasons, packing checks and locations: tabs, so the tab is in the URL. */
const shopFloorSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/shop-floor",
  validateSearch: tabSearch,
  component: ShopFloorSetupPage,
});

const manualInvoicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/manual-invoices",
  component: ManualInvoicesPage,
});

const partsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/parts",
  component: PartsPage,
});

const partDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  validateSearch: tabSearch,
  path: "/parts/$partId",
  component: function PartDetailRoute() {
    const { partId } = partDetailRoute.useParams();
    return <PartDetailPage partId={Number(partId)} key={partId} />;
  },
});

// The native part master. /parts stays the SIGM reader until the pipeline
// reads from here instead; two paths that each mean one thing.
const manufacturingPartsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/manufacturing/parts",
  component: ManufacturingPartsPage,
});

const routingReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/manufacturing/routings",
  component: RoutingReviewPage,
});

const manufacturingPartDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  validateSearch: tabSearch,
  path: "/manufacturing/parts/$partId",
  component: function ManufacturingPartDetailRoute() {
    const { partId } = manufacturingPartDetailRoute.useParams();
    return <ManufacturingPartDetailPage partId={Number(partId)} key={partId} />;
  },
});

// The product configurator's data (docs/product-configurator/design.md).
const engineeringColoursRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engineering/colours",
  component: ColoursPage,
});

const engineeringModelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engineering/models",
  component: ProductModelsPage,
});

const engineeringCleanupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engineering/cleanup",
  component: CleanupPage,
});

const stockRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/manufacturing/stock",
  component: StockPage,
});

const stockAdjustRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/manufacturing/stock/adjust",
  component: StockAdjustmentPage,
});

const stockCountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/manufacturing/stock/counts",
  component: StockCountsPage,
});

const stockCountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/manufacturing/stock/counts/$countId",
  component: function StockCountRoute() {
    const { countId } = stockCountRoute.useParams();
    return <StockCountPage countId={Number(countId)} />;
  },
});

const productionScrapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/scrap",
  component: ScrapReportPage,
});

const inactiveStockRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/manufacturing/stock/inactive",
  component: InactiveStockPage,
});

const stockingPointsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/manufacturing/stock/points",
  component: StockingPointsPage,
});

const stockTransferRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/manufacturing/stock/transfer",
  component: StockTransferPage,
});

const replenishmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purchasing/replenishment",
  component: ReplenishmentPage,
});

const cashProjectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purchasing/cash",
  component: CashProjectionPage,
});

const purchasingReviewsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purchasing/reviews",
  component: PurchasingReviewsPage,
});

const truckGroupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purchasing/order-units",
  component: OrderUnitsPage,
});

const purchaseOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purchasing/purchase-orders",
  component: PurchaseOrdersPage,
});

const purchaseOrderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  validateSearch: tabSearch,
  path: "/purchasing/purchase-orders/$orderId",
  component: function PurchaseOrderDetailRoute() {
    const { orderId } = purchaseOrderDetailRoute.useParams();
    return <PurchaseOrderDetailPage orderId={Number(orderId)} key={orderId} />;
  },
});

const purchaseOrderPrintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purchasing/purchase-orders/$orderId/print",
  component: function PurchaseOrderPrintRoute() {
    const { orderId } = purchaseOrderPrintRoute.useParams();
    return <PurchaseOrderPrintPage orderId={Number(orderId)} />;
  },
});

/** `?tv=1` opens the board in TV mode: the screen on the plant's wall. */
const shopFloorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/floor",
  validateSearch: (search: Record<string, unknown>): { tv?: number } =>
    search.tv === 1 || search.tv === "1" || search.tv === true ? { tv: 1 } : {},
  component: function ShopFloorRoute() {
    const { tv } = shopFloorRoute.useSearch();
    return <ShopFloorBoardPage tv={tv === 1} />;
  },
});

/** Fabrication's kiosk offers Laval's stations; Assembly's, Terrebonne's. */
const stationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/station",
  component: function FabricationStation() {
    return <StationKioskPage site="LAVAL" key="LAVAL" />;
  },
});

const assemblyStationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assembly/station",
  component: function AssemblyStation() {
    return <StationKioskPage site="TERREBONNE" key="TERREBONNE" />;
  },
});

// Cut jobs: the lots Cut Rite batched, cut as one (docs/cutting-jobs).
const cutJobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/cut-jobs",
  component: CutJobsPage,
});

const cutJobDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/cut-jobs/$jobId",
  component: function CutJobDetailRoute() {
    const { jobId } = cutJobDetailRoute.useParams();
    return <CutJobDetailPage jobId={Number(jobId)} key={jobId} />;
  },
});

const productionOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/orders",
  component: function FabricationOrders() {
    return <ProductionOrdersPage scope="fabrication" key="fabrication" />;
  },
});

const assemblyOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assembly/orders",
  component: function AssemblyOrders() {
    return <ProductionOrdersPage scope="assembly" key="assembly" />;
  },
});

const productionOrderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  validateSearch: tabSearch,
  path: "/production/orders/$orderId",
  component: function ProductionOrderDetailRoute() {
    const { orderId } = productionOrderDetailRoute.useParams();
    return <ProductionOrderDetailPage orderId={Number(orderId)} key={orderId} />;
  },
});

/** Assembly's shop floor: the Terrebonne board. Its old address still leads here. */
const terrebonneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assembly/floor",
  // ?tv=1 is the shop monitor's view.
  validateSearch: (search: Record<string, unknown>): { tv?: number } =>
    search.tv === 1 || search.tv === "1" || search.tv === true ? { tv: 1 } : {},
  component: function TerrebonneRoute() {
    const { tv } = terrebonneRoute.useSearch();
    return <TerrebonnePage tv={tv === 1} />;
  },
});

const oldTerrebonneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/terrebonne",
  validateSearch: (search: Record<string, unknown>): { tv?: number } =>
    search.tv === 1 || search.tv === "1" || search.tv === true ? { tv: 1 } : {},
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/assembly/floor", search });
  },
});

const shelvesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/shelves",
  component: ShelvesPage,
});

const serviceProductionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/servicedesk/production",
  component: ServiceProductionQueuePage,
});

const serviceOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/servicedesk/orders",
  component: ServiceOrdersPage,
});

const serviceOrderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  validateSearch: tabSearch,
  path: "/servicedesk/orders/$orderId",
  component: function ServiceOrderDetailRoute() {
    const { orderId } = serviceOrderDetailRoute.useParams();
    return <ServiceOrderDetailPage orderId={Number(orderId)} key={orderId} />;
  },
});

const suppliersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/manufacturing/suppliers",
  component: SuppliersPage,
});

const supplierDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/manufacturing/suppliers/$supplierId",
  component: function SupplierDetailRoute() {
    const { supplierId } = supplierDetailRoute.useParams();
    return <SupplierDetailPage supplierId={Number(supplierId)} key={supplierId} />;
  },
});

const servicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/services",
  component: ServicesPage,
});

const serviceDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  validateSearch: tabSearch,
  path: "/services/$sku",
  component: function ServiceDetailRoute() {
    const { sku } = serviceDetailRoute.useParams();
    return <ServiceDetailPage sku={sku} key={sku} />;
  },
});

const pricingRoutes = (
  [
    ["/pricing/product", PricingPage],
    ["/pricing/product/discount", ProductDiscountPage],
    ["/pricing/rules", PricingRulesPage],
  ] as const
).map(([path, component]) =>
  createRoute({ getParentRoute: () => rootRoute, path, component }),
);

const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products",
  component: ProductsPage,
});

const productDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  validateSearch: tabSearch,
  path: "/products/$productId",
  component: function ProductDetailRoute() {
    const { productId } = productDetailRoute.useParams();
    return <ProductDetailPage productId={Number(productId)} key={productId} />;
  },
});

const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/project",
  component: ProjectsPage,
});

const apiUpdatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/api-inventory-updates",
  component: ApiInventoryUpdatesPage,
});

const salesChannelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales-channels",
  component: SalesChannelsPage,
});

const shopifyStoreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shopify/store",
  component: ShopifyStorePage,
});

const pipelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/pipeline",
  component: PipelinePage,
});

const transfersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production/transfers",
  component: TransferPlanPage,
});

const appAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app-accounts",
  component: AppAccountsPage,
});

const stubRoutes = stubs.map(({ path, titleKey, view }) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: () => <PlaceholderPage titleKey={titleKey} view={view} />,
  }),
);

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: AccountPage,
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([indexRoute, accountRoute, usersRoute, appAccountsRoute, actionsRoute, projectsRoute, apiUpdatesRoute, salesChannelsRoute, shopifyStoreRoute, pipelineRoute, transfersRoute, productsRoute, productDetailRoute, clientMarginRoute, salesAnalysisRoute, invoicesRoute, ordersRoute, partsRoute, partDetailRoute, manufacturingPartsRoute, routingReviewRoute, manufacturingPartDetailRoute, engineeringColoursRoute, engineeringModelsRoute, engineeringCleanupRoute, stockRoute, stockAdjustRoute, stockCountsRoute, stockCountRoute, stockTransferRoute, inactiveStockRoute, stockingPointsRoute, replenishmentRoute, cashProjectionRoute, purchasingReviewsRoute, truckGroupsRoute, purchaseOrdersRoute, purchaseOrderDetailRoute, purchaseOrderPrintRoute, productionOrdersRoute, productionOrderDetailRoute, cutJobsRoute, cutJobDetailRoute, productionScrapRoute, shopFloorRoute, stationRoute, assemblyStationRoute, assemblyOrdersRoute, terrebonneRoute, oldTerrebonneRoute, shelvesRoute, serviceOrdersRoute, serviceOrderDetailRoute, serviceProductionRoute, suppliersRoute, supplierDetailRoute, servicesRoute, serviceDetailRoute, ...configRoutes, shopFloorSetupRoute, ...pricingRoutes, manualInvoicesRoute, ...stubRoutes]),
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
